import { computed, ref, shallowReactive } from '@vue/composition-api';
import {
  AtoneAuthenticateGetResponse,
  AtoneMemberTypePostResponse,
  AtoneRequestTokenPostResponse,
  // AtoneShopOptionGetResponse,
  AtoneTracksPostResponse,
  AtoneTracksTrustResponse,
  AtonePointGetResponse,
  AtoneMemberRegisterPostResponse,
  AtoneMemberValidatePostRequest,
  AtoneCapturePostResponse,
  AtoneAuthenticatePostResponse,
  AtoneFufururuTokenPostResponse,
  AtoneFufururuInformGetResponse,
  AtoneZipAddressGetResponse,
  AtoneReauthorizePutResponse,
  AtoneSmsNotifyPostResponse,
  RegistTransactionPostResponse,
  AtoneMemberTypePostResponseMemberTypeEnum,
  GuestAuthenticatePostResponse,
  GuestAuthenticationTokenGetResponse,
  GuestFingerprintPostResponse,
  GuestRegisterPostResponse,
  GuestSmsAuthenticateNotifyPostResponse,
  GuestSmsAuthenticateVerifyPostResponse,
  GuestTypePostResponse,
  LiteCapturePostResponse,
  GuestUpdatePostResponse,
  AtoneMethodGetResponse,
  AtoneSplitCapturePostResponse,
  AtoneShopV2OptionGetResponse,
} from '@netprotections/berlin-bff';
import { createContext } from '@berlin-front/common/composables/context';
import { CamelCasedPropertiesDeep } from 'type-fest';
import { requiredRef, simpleSetValue } from '@berlin-front/common/utils';

import type { atoneSettlementServiceParameterValidator } from '@schemas/atone-settlement/AtoneSettlementParameterSchema';
import { atoneLiteSettlementServiceParameterValidator } from '@schemas/atone-lite-settlement/AtoneLiteSettlementParameterSchema';
import { atoneLiteAuthServiceParameterValidator } from '@schemas/atone-lite-auth/AtoneLiteAuthParameterSchema';
import { atoneAuthServiceParameterValidator } from '@schemas/atone-auth/AtoneAuthParameterSchema';
import { npAtobaraiSettlementServiceParameterValidator } from '@schemas/np-atobarai-settlement/NpAtobaraiSettlementParameterSchema';

type ServiceType = string;
type AtoneModalParameter = CamelCasedPropertiesDeep<typeof atoneSettlementServiceParameterValidator._output>;
type AtoneAuthModalParameter = CamelCasedPropertiesDeep<typeof atoneAuthServiceParameterValidator._output>;
type AtoneLiteModalParameter = CamelCasedPropertiesDeep<typeof atoneLiteSettlementServiceParameterValidator._output>;
type AtoneLiteAuthModalParameter = CamelCasedPropertiesDeep<typeof atoneLiteAuthServiceParameterValidator._output>;
type NpAtobaraiModalParameter = CamelCasedPropertiesDeep<typeof npAtobaraiSettlementServiceParameterValidator._output>;

export const ApiCacheContext = createContext(() => {
  // Liteゲストログイン実行時のphoneNumberとemail
  const atoneLiteGuestLoginAccount = ref<{ phoneNumber: string; email: string } | undefined>();
  const setAtoneLiteGuestLoginAccount = simpleSetValue(atoneLiteGuestLoginAccount);

  // Lite新規登録のphoneNumberとemail
  const atoneLiteNewAccount = ref<{ phoneNumber: string; email: string } | undefined>();
  const setAtoneLiteNewAccount = simpleSetValue(atoneLiteNewAccount);

  // Atoneの認証パラメータ。
  const atoneModalParameterRef = ref<AtoneModalParameter | undefined>();
  const atoneModalParameter = requiredRef(atoneModalParameterRef);
  const setAtoneModalParameter = simpleSetValue(atoneModalParameterRef);

  // AtoneLiteの認証パラメータ。
  const atoneLiteModalParameterRef = ref<AtoneLiteModalParameter | undefined>();
  const atoneLiteModalParameter = requiredRef(atoneLiteModalParameterRef);
  const setAtoneLiteModalParameter = simpleSetValue(atoneLiteModalParameterRef);

  // AtoneAuthの認証パラメータ
  const atoneAuthModalParameterRef = ref<AtoneAuthModalParameter | undefined>();
  const atoneAuthModalParameter = requiredRef(atoneAuthModalParameterRef);
  const setAtoneAuthModalParameter = simpleSetValue(atoneAuthModalParameterRef);

  // AtoneLiteの認証パラメータ。
  const atoneLiteAuthModalParameterRef = ref<AtoneLiteAuthModalParameter | undefined>();
  const atoneLiteAuthModalParameter = requiredRef(atoneLiteAuthModalParameterRef);
  const setAtoneLiteAuthModalParameter = simpleSetValue(atoneLiteAuthModalParameterRef);

  /**
   * AtoneのpubKey
   *
   * atoneのpubKeyは、それぞれのサービスで全て同じはずなのでここでまとめちゃう。
   * サービス間でまたがる画面でもpubKeyを共通コードでアクセスできる。
   */
  const atonePubKey = computed(() => {
    const pubKey =
      atoneModalParameterRef.value?.pubKey ||
      atoneLiteModalParameterRef.value?.pubKey ||
      atoneAuthModalParameterRef.value?.pubKey ||
      atoneLiteAuthModalParameterRef.value?.pubKey;
    if (typeof pubKey === 'string') return pubKey;
    throw new Error('pub_keyが設定されていません。');
  });

  // NP後払いの認証パラメータ。
  const npAtobaraiModalParameterRef = ref<NpAtobaraiModalParameter | undefined>(undefined);
  const npAtobaraiModalParameter = requiredRef(npAtobaraiModalParameterRef);
  const setNpAtobaraiModalParameter = simpleSetValue(npAtobaraiModalParameterRef);

  const currentServiceTypeRef = ref<ServiceType | undefined>(undefined);

  // spiderでV2にバージョンアップ
  // const atoneShopOptionGetResult = ref<AtoneShopOptionGetResponse | undefined>(undefined);
  // const setAtoneShopOptionGetResult = simpleSetValue(atoneShopOptionGetResult);

  // spider
  const atoneMethodGetResult = ref<AtoneMethodGetResponse | undefined>(undefined);
  const setPaymentMethodGetResult = simpleSetValue(atoneMethodGetResult);

  const atoneSplitCapturePostResult = ref<AtoneSplitCapturePostResponse | undefined>(undefined);
  const setAtoneSplitCapturePostResult = simpleSetValue(atoneSplitCapturePostResult);

  const atoneShopV2OptionGetResult = ref<AtoneShopV2OptionGetResponse | undefined>(undefined);
  const setAtoneShopV2OptionGetResult = simpleSetValue(atoneShopV2OptionGetResult);

  const atoneRequestTokenPostResult = ref<AtoneRequestTokenPostResponse | undefined>(undefined);
  const setAtoneRequestTokenPostResult = simpleSetValue(atoneRequestTokenPostResult);

  const atoneAuthenticateGetResult = ref<AtoneAuthenticateGetResponse | undefined>(undefined);
  const setAtoneAuthenticateGetResult = simpleSetValue(atoneAuthenticateGetResult);

  // ↓ ここより下はログアウト実行時にリセットする。

  const atoneMemberTypePostResult = ref<AtoneMemberTypePostResponse | undefined>(undefined);
  const setAtoneMemberTypePostResult = simpleSetValue(atoneMemberTypePostResult);

  const atoneMemberRegisterPostResult = ref<AtoneMemberRegisterPostResponse | undefined>(undefined);
  const setAtoneMemberRegisterPostResult = simpleSetValue(atoneMemberRegisterPostResult);

  const atoneTracksPostResult = ref<AtoneTracksPostResponse | undefined>(undefined);
  const setAtoneTracksPostResult = simpleSetValue(atoneTracksPostResult);

  const atoneTracksTrustResult = ref<AtoneTracksTrustResponse | undefined>(undefined);
  const setAtoneTracksTrustResult = simpleSetValue(atoneTracksTrustResult);

  const atonePointGetResult = ref<AtonePointGetResponse | undefined>(undefined);
  const setAtonePointGetResult = simpleSetValue(atonePointGetResult);

  // fufururu会員情報取得
  const atoneFufururuInformGetResult = ref<AtoneFufururuInformGetResponse | undefined>(undefined);
  const setAtoneFufururuInformGetResult = simpleSetValue(atoneFufururuInformGetResult);

  // atone会員登録情報(validation完了)確認画面データ連携用
  const atoneMemberValidatePostRequestParam = ref<AtoneMemberValidatePostRequest | undefined>(undefined);
  const setAtoneMemberValidatePostRequestParam = simpleSetValue(atoneMemberValidatePostRequestParam);

  const atoneCapturePostResult = ref<AtoneCapturePostResponse | undefined>(undefined);
  const setAtoneCapturePostResult = simpleSetValue(atoneCapturePostResult);

  const atoneAuthenticatePostResult = ref<AtoneAuthenticatePostResponse | undefined>(undefined);
  const setAtoneAuthenticatePostResult = simpleSetValue(atoneAuthenticatePostResult);

  const atoneFufururuTokenPostResult = ref<AtoneFufururuTokenPostResponse | undefined>(undefined);
  const setAtoneFufururuTokenPostResult = simpleSetValue(atoneFufururuTokenPostResult);

  const atoneZipAddressGetResult = ref<AtoneZipAddressGetResponse | undefined>(undefined);
  const setAtoneZipAddressGetResult = simpleSetValue(atoneZipAddressGetResult);

  const atoneReauthorizePutResult = ref<AtoneReauthorizePutResponse | undefined>(undefined);
  const setAtoneReauthorizePutResult = simpleSetValue(atoneReauthorizePutResult);

  const atoneSmsNotifyPostResult = ref<AtoneSmsNotifyPostResponse | undefined>(undefined);
  const setAtoneSmsNotifyPostResult = simpleSetValue(atoneSmsNotifyPostResult);

  const registTransactionPostResult = ref<RegistTransactionPostResponse | undefined>(undefined);
  const setRegistTransactionPostResult = simpleSetValue(registTransactionPostResult);

  const registerMemberName = ref<string | undefined>(undefined);
  const setRegisterMemberName = (memberName: string | undefined) => {
    registerMemberName.value = memberName;
  };

  // napoleon
  const guestAuthenticatePostResult = ref<GuestAuthenticatePostResponse | undefined>(undefined);
  const setGuestAuthenticatePostResult = simpleSetValue(guestAuthenticatePostResult);

  const guestAuthenticationTokenGetResult = ref<GuestAuthenticationTokenGetResponse | undefined>(undefined);
  const setGuestAuthenticationTokenGetResult = simpleSetValue(guestAuthenticationTokenGetResult);

  const guestFingerprintPostResult = ref<GuestFingerprintPostResponse | undefined>(undefined);
  const setGuestFingerprintPostResult = simpleSetValue(guestFingerprintPostResult);

  const guestRegisterPostResult = ref<GuestRegisterPostResponse | undefined>(undefined);
  const setGuestRegisterPostResult = simpleSetValue(guestRegisterPostResult);

  const guestSmsAuthenticateNotifyPostResult = ref<GuestSmsAuthenticateNotifyPostResponse | undefined>(undefined);
  const setGuestSmsAuthenticateNotifyPostResult = simpleSetValue(guestSmsAuthenticateNotifyPostResult);

  const guestSmsAuthenticateVerifyPostResult = ref<GuestSmsAuthenticateVerifyPostResponse | undefined>(undefined);
  const setGuestSmsAuthenticateVerifyPostResult = simpleSetValue(guestSmsAuthenticateVerifyPostResult);

  const guestTypePostResult = ref<GuestTypePostResponse | undefined>(undefined);
  const setGuestTypePostResult = simpleSetValue(guestTypePostResult);

  const liteCapturePostResult = ref<LiteCapturePostResponse | undefined>(undefined);
  const setLiteCapturePostResult = simpleSetValue(liteCapturePostResult);

  const guestUpdatePostResult = ref<GuestUpdatePostResponse | undefined>(undefined);
  const setGuestUpdatePostResult = simpleSetValue(guestUpdatePostResult);

  const trustedTrackToken = computed(() =>
    // eslint-disable-next-line no-nested-ternary
    atoneTracksTrustResult.value?.trustedFlag
      ? atoneTracksPostResult.value?.trackToken
      : guestFingerprintPostResult.value?.trustedFlag
        ? guestFingerprintPostResult.value?.trackToken
        : undefined,
  );

  const npToken = ref<string | undefined>();
  const setNpToken = simpleSetValue(npToken);
  const signature = ref<string | undefined>();
  const setSignature = simpleSetValue(signature);

  const setNpTokenAndSignature = (tokenValue: string | undefined, signatureValue?: string | undefined) => {
    setNpToken(tokenValue);
    setSignature(signatureValue);
  };

  const userFullName = computed(
    () =>
      atoneAuthenticateGetResult.value?.fullName ||
      atoneAuthenticatePostResult.value?.fullName ||
      guestAuthenticationTokenGetResult.value?.fullName ||
      guestSmsAuthenticateVerifyPostResult.value?.fullName ||
      guestUpdatePostResult.value?.fullName ||
      registerMemberName.value ||
      '',
  );

  const tokens = computed(() => ({
    requestToken: atoneRequestTokenPostResult.value?.requestToken,
    guestRequestToken: guestAuthenticatePostResult.value?.guestRequestToken,
    npToken: npToken.value || '',
    trustedTrackToken: trustedTrackToken.value,
  }));

  const logout = () => {
    (
      [
        setAtoneAuthenticateGetResult,
        setAtoneMemberRegisterPostResult,
        setAtoneTracksPostResult,
        setAtoneTracksTrustResult,
        setAtonePointGetResult,
        setAtoneFufururuInformGetResult,
        setAtoneMemberValidatePostRequestParam,
        setAtoneCapturePostResult,
        setAtoneAuthenticatePostResult,
        setAtoneFufururuTokenPostResult,
        setAtoneZipAddressGetResult,
        setAtoneReauthorizePutResult,
        setAtoneSmsNotifyPostResult,
        setRegistTransactionPostResult,
        setRegisterMemberName,
        setAtoneLiteGuestLoginAccount,
        setAtoneLiteNewAccount,
        setGuestAuthenticatePostResult,
        setGuestAuthenticationTokenGetResult,
        setGuestTypePostResult,
        setGuestFingerprintPostResult,
        setGuestRegisterPostResult,
        setGuestSmsAuthenticateNotifyPostResult,
        setGuestSmsAuthenticateVerifyPostResult,
        setGuestUpdatePostResult,
        setNpToken,
        setSignature,
        // そのままでも型エラーではないが、ここの型推論は異様に重いため変換する。
      ] as ((value: undefined) => void)[]
    ).forEach((setter) => setter(undefined));
    setAtoneMemberTypePostResult({
      memberType: AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_0,
    });
  };

  const reset = () => {
    (
      [
        setAtoneRequestTokenPostResult,
        setAtoneAuthenticateGetResult,
        setAtoneMemberTypePostResult,
        setAtoneMemberRegisterPostResult,
        setAtoneTracksPostResult,
        setAtoneTracksTrustResult,
        setAtonePointGetResult,
        setAtoneFufururuInformGetResult,
        setAtoneMemberValidatePostRequestParam,
        setAtoneCapturePostResult,
        setAtoneAuthenticatePostResult,
        setAtoneFufururuTokenPostResult,
        setAtoneZipAddressGetResult,
        setAtoneReauthorizePutResult,
        setAtoneSmsNotifyPostResult,
        setRegistTransactionPostResult,
        setRegisterMemberName,
        setAtoneLiteGuestLoginAccount,
        setAtoneLiteNewAccount,
        setGuestAuthenticatePostResult,
        setGuestAuthenticationTokenGetResult,
        setGuestTypePostResult,
        setGuestFingerprintPostResult,
        setGuestRegisterPostResult,
        setGuestSmsAuthenticateNotifyPostResult,
        setGuestSmsAuthenticateVerifyPostResult,
        setGuestUpdatePostResult,
        setNpToken,
        setSignature,
        // そのままでも型エラーではないが、ここの型推論は異様に重いため変換する。
      ] as ((value: undefined) => void)[]
    ).forEach((set) => set(undefined));
  };

  // shallowReactiveにすると、ネストした階層のreactive要素の再編成が行われなくなる。
  // 通常、reactiveのプロパティにrefが存在する場合、refは展開されてしまうが、展開させたくないためshallowReactiveで囲っている。
  return shallowReactive({
    userFullName,

    signature,
    setNpTokenAndSignature,

    atoneLiteGuestLoginAccount,
    setAtoneLiteGuestLoginAccount,

    atoneLiteNewAccount,
    setAtoneLiteNewAccount,

    atoneModalParameter,
    setAtoneModalParameter,

    atoneLiteModalParameter,
    setAtoneLiteModalParameter,

    atoneAuthModalParameter,
    setAtoneAuthModalParameter,

    atoneLiteAuthModalParameter,
    setAtoneLiteAuthModalParameter,

    npAtobaraiModalParameter,
    setNpAtobaraiModalParameter,

    currentServiceTypeRef,

    // spiderでV2にバージョンアップ
    // atoneShopOptionGetResult,
    // setAtoneShopOptionGetResult,

    atoneMethodGetResult,
    setPaymentMethodGetResult,

    atoneSplitCapturePostResult,
    setAtoneSplitCapturePostResult,

    atoneShopV2OptionGetResult,
    setAtoneShopV2OptionGetResult,

    atoneAuthenticateGetResult,
    setAtoneAuthenticateGetResult,

    atoneRequestTokenPostResult,
    setAtoneRequestTokenPostResult,

    atoneMemberTypePostResult,
    setAtoneMemberTypePostResult,

    atoneMemberRegisterPostResult,
    setAtoneMemberRegisterPostResult,

    atoneTracksPostResult,
    setAtoneTracksPostResult,

    atoneTracksTrustResult,
    setAtoneTracksTrustResult,

    atonePointGetResult,
    setAtonePointGetResult,

    atoneMemberValidatePostRequestParam,
    setAtoneMemberValidatePostRequestParam,

    atoneCapturePostResult,
    setAtoneCapturePostResult,

    atoneAuthenticatePostResult,
    setAtoneAuthenticatePostResult,

    atoneFufururuTokenPostResult,
    setAtoneFufururuTokenPostResult,

    atoneFufururuInformGetResult,
    setAtoneFufururuInformGetResult,

    atoneZipAddressGetResult,
    setAtoneZipAddressGetResult,

    atoneReauthorizePutResult,
    setAtoneReauthorizePutResult,

    atoneSmsNotifyPostResult,
    setAtoneSmsNotifyPostResult,

    registTransactionPostResult,
    setRegistTransactionPostResult,

    registerMemberName,
    setRegisterMemberName,

    guestAuthenticatePostResult,
    setGuestAuthenticatePostResult,

    guestAuthenticationTokenGetResult,
    setGuestAuthenticationTokenGetResult,

    guestFingerprintPostResult,
    setGuestFingerprintPostResult,

    guestRegisterPostResult,
    setGuestRegisterPostResult,

    guestSmsAuthenticateNotifyPostResult,
    setGuestSmsAuthenticateNotifyPostResult,

    guestSmsAuthenticateVerifyPostResult,
    setGuestSmsAuthenticateVerifyPostResult,

    guestTypePostResult,
    setGuestTypePostResult,

    liteCapturePostResult,
    setLiteCapturePostResult,

    guestUpdatePostResult,
    setGuestUpdatePostResult,

    tokens,
    // ログアウト時のリセット用。
    logout,

    // ホームに戻った時のリセット。
    reset,

    atonePubKey,
  });
}, 'APICacheContext');
